/**
 * Anchor-Scrolling ohne Hash in der URL.
 *
 * Ein Hash in der Adresszeile macht Anker-Links einmalig: klickt man denselben
 * Link erneut, ändert sich die URL nicht, der Browser navigiert nicht und es
 * wird nicht mehr gescrollt. Deshalb wird hier immer selbst gescrollt und der
 * Hash nie in die URL geschrieben.
 *
 * Für Ziele auf einer anderen Seite wird der Anker über den sessionStorage an
 * die nächste Seite durchgereicht und dort abgeholt (siehe AnchorScroll).
 */

const PENDING_KEY = "pending-anchor";

/** Normalisiert einen Pfad für den Vergleich mit `usePathname()`. */
export function normalizePath(path: string): string {
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const withoutTrailing = withSlash.replace(/\/+$/, "");
  return withoutTrailing || "/";
}

/** Zerlegt ein Link-Ziel in Pfad und Anker (ohne `#`). */
export function splitAnchor(href: string): { path: string; anchor: string } {
  const index = href.indexOf("#");
  if (index === -1) return { path: href, anchor: "" };
  return { path: href.slice(0, index), anchor: href.slice(index + 1) };
}

/** Das Element zu einem Anker – über `id` oder `name`. */
function findAnchor(anchor: string): HTMLElement | null {
  if (!anchor) return null;
  const byId = document.getElementById(anchor);
  if (byId) return byId;
  return document.querySelector<HTMLElement>(`[name="${CSS.escape(anchor)}"]`);
}

/** Scrollt zum Anker, wenn er existiert. Gibt zurück, ob gescrollt wurde. */
export function scrollToAnchor(anchor: string): boolean {
  const target = findAnchor(anchor);
  if (!target) return false;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}

/**
 * Merkt sich den Anker für die Seite, zu der gerade navigiert wird.
 * sessionStorage, damit es auch einen vollen Reload übersteht.
 */
export function setPendingAnchor(anchor: string): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, anchor);
  } catch {
    /* sessionStorage kann blockiert sein – dann eben ohne Anker-Sprung. */
  }
}

export function hasPendingAnchor(): boolean {
  try {
    return Boolean(window.sessionStorage.getItem(PENDING_KEY));
  } catch {
    return false;
  }
}

/** Liest den gemerkten Anker aus und entfernt ihn. */
export function consumePendingAnchor(): string | null {
  try {
    const anchor = window.sessionStorage.getItem(PENDING_KEY);
    if (anchor) window.sessionStorage.removeItem(PENDING_KEY);
    return anchor;
  } catch {
    return null;
  }
}

/**
 * Scrollt zum Anker, sobald er im DOM ist. Nach einem Seitenwechsel werden
 * Inhalte gestreamt und Bilder verschieben das Layout noch – deshalb wird bis
 * `timeout` weiter gesucht und die Position kurz nachgezogen.
 */
export function scrollToAnchorWhenReady(anchor: string, timeout = 3000): void {
  const deadline = Date.now() + timeout;

  const attempt = () => {
    if (scrollToAnchor(anchor)) {
      // Nachlaufende Layout-Sprünge (Bilder, Fonts, AOS) korrigieren.
      window.setTimeout(() => scrollToAnchor(anchor), 350);
      return;
    }
    if (Date.now() < deadline) window.requestAnimationFrame(attempt);
  };

  window.requestAnimationFrame(attempt);
}
